/**
 * CounterpartyPicker (#2678 c) — the optional "Gegenpartei" field in
 * TransactionFormModal. Lets the farmer link one transaction to an existing
 * ``organization`` entity; the chosen id rides to the wire as
 * ``counterparty_id`` (conditional key — see ``optimisticPlanned.ts``).
 *
 * **Reuse, not a new endpoint.** There was no organization-search surface in the
 * FE before this: ``hooks/`` had none and ``TransactionTwinLinks`` only READS
 * existing links. Rather than mint a bespoke route, this rides the shared twin
 * entity reads — ``useEntities('organization', …, {q})`` (backed by
 * ``GET /v1/twin/entities/{type}``, whose ``q`` is a case-insensitive substring
 * scan over ``entity_id`` + ``attributes::text``, server-side paginated) for the
 * search, and ``useEntitiesByIds`` (``POST /v1/twin/entities/{type}/query``) to
 * name an already-linked org. Both are tenant-scoped, already cached (60 s
 * staleTime) and already exercised by /farm, so this adds no contract to own.
 * The one thing reuse costs is that ``q`` matches any attribute, not just the
 * name — an acceptable trade for a lookup field, and strictly more forgiving.
 *
 * **Both reads live in conditionally-mounted CHILDREN**, which is a design
 * property and not an accident:
 *  - a form opened without touching this field constructs no query at all, so the
 *    optional picker costs nothing on the overwhelmingly common save;
 *  - and every pre-existing TransactionFormModal test (plus the #2678-a parity
 *    snapshots) keeps rendering with no QueryClientProvider.
 *
 * **No resolution heuristics, deliberately.** Nothing here reads
 * ``counterparty_iban`` or the raw ``counterparty`` text to pre-select an org:
 * that IBAN field is IBAN-SHAPED, not verified (#2678-b measured only 47 % of the
 * real WF dump's shape-valid values as passing mod-97, the remainder a MIX of SEPA
 * Gläubiger-IDs and short-by-one IBANs). **#2741 has since split and checksum-gated
 * that field**, so the mis-keying objection is spent — but the rule stands on its own
 * first reason: a guessed link is, once stored, indistinguishable from a farmer's — and #2736 notes prod still carries the
 * pre-#2680 writer's ~4.550 singleton counterparties, so anything calibrated
 * against today's pool would bake in an artefact the backfill re-run erases.
 * The farmer picks; this component only lists what she typed for.
 */
import { useEffect, useState } from 'react';
import { useEntities } from '../../../hooks/useTwin';
import CounterpartyName from './CounterpartyName';
import {
  COUNTERPARTY_MIN_QUERY_LENGTH,
  counterpartySearchTerm,
  organizationOptions,
} from './counterparty';
import './CounterpartyPicker.css';

/** Hits shown at once. Small on purpose: this is a "find the one you mean" field,
 *  not a browser — past a handful the answer is to type more, which the
 *  "N weitere" note says out loud. */
const PAGE_SIZE = 8;

/** Idle-time before a typed term reaches the wire. Long enough that a word typed
 *  at speed is one read, short enough to feel immediate. */
const DEBOUNCE_MS = 250;

const ENTITY_TYPE = 'organization';

export interface CounterpartyPickerProps {
  /** The currently linked ``organization`` id, if any. */
  value?: string;
  /** ``undefined`` on clear — NOT ``''`` — so the caller's conditional spread
   *  drops the wire key rather than sending an empty link. */
  onChange: (counterpartyId: string | undefined) => void;
}

/** Names an already-linked organization. Mounted only while a link is held.
 *
 *  The naming itself moved to `CounterpartyName` (#3143) so the ledger's Gegenpartei
 *  row and this picker cannot grow two answers to "what is this org called" — its
 *  three-state rule (resolved / `Wird geladen …` / raw id, the last never doubling
 *  as the loading state) is documented there. This keeps only what is genuinely the
 *  picker's: the selected-state chrome and the Entfernen button. `CounterpartyName`
 *  renders the two spans as a fragment, so this DOM is unchanged. */
function LinkedCounterparty({ id, onClear }: { id: string; onClear: () => void }) {
  return (
    <div className="liq-cp__selected">
      <CounterpartyName
        id={id}
        className="liq-cp__selected-label"
        sublabelClassName="liq-cp__selected-type"
      />
      <button type="button" className="liq-cp__clear" aria-label="Gegenpartei entfernen" onClick={onClear}>
        Entfernen
      </button>
    </div>
  );
}

/** Searches organizations for one term. Mounted only for a term worth a read. */
function CounterpartyResults({ term, onPick }: { term: string; onPick: (id: string) => void }) {
  const { entities, total, primaryKey, isLoading, isError } = useEntities(ENTITY_TYPE, undefined, {
    paginated: true,
    pageSize: PAGE_SIZE,
    q: term,
  });
  const options = organizationOptions(entities, primaryKey);

  // An errored read is NOT "you have no organizations" — speaking it that way would
  // assert something false about the tenant's own data. Failure gets its own alert,
  // and only a read that actually answered may say "nothing found".
  if (isError && options.length === 0) {
    return (
      <span className="liq-cp__note liq-cp__note--error" role="alert">
        Organisationen konnten nicht durchsucht werden.
      </span>
    );
  }
  if (isLoading && options.length === 0) {
    return (
      <span className="liq-cp__note" role="status">
        Suche läuft …
      </span>
    );
  }
  if (options.length === 0) {
    return (
      <span className="liq-cp__note" role="status">
        Keine Organisation gefunden.
      </span>
    );
  }

  // Measured against the page the SERVER returned, not the de-duplicated list: rows
  // ``organizationOptions`` collapsed are still rows this page covered, so counting
  // them as "further hits" would send the farmer searching for a duplicate.
  const hidden = Math.max(0, total - entities.length);
  return (
    <>
      <ul className="liq-cp__results">
        {options.map((o) => (
          <li key={o.id}>
            <button type="button" className="liq-cp__result" onClick={() => onPick(o.id)}>
              <span className="liq-cp__result-label">{o.label}</span>
              {o.sublabel && <span className="liq-cp__result-type">{o.sublabel}</span>}
            </button>
          </li>
        ))}
      </ul>
      {/* Say when the list is capped: otherwise an org that exists but sorted past
          the page reads as "not in the system". */}
      {hidden > 0 && (
        <span className="liq-cp__note" role="status">
          {hidden} weitere Treffer — Suche verfeinern.
        </span>
      )}
    </>
  );
}

export default function CounterpartyPicker({ value, onChange }: CounterpartyPickerProps) {
  const [raw, setRaw] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(raw), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [raw]);

  // The hint keys off the RAW text (immediate feedback), the read off the debounced
  // one (one request per word, not per keystroke).
  const term = counterpartySearchTerm(debounced);
  const tooShort = raw.trim().length > 0 && counterpartySearchTerm(raw) === null;

  const pick = (id: string) => {
    onChange(id);
    setRaw('');
    setDebounced('');
  };

  return (
    <div className="liq-txform__field liq-cp">
      <span className="liq-cp__label">Gegenpartei</span>
      {value ? (
        <LinkedCounterparty id={value} onClear={() => onChange(undefined)} />
      ) : (
        <>
          <input
            aria-label="Gegenpartei suchen"
            type="text"
            placeholder="Organisation suchen …"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            // This input sits inside the modal's <form>, which has a submit button, so
            // a browser IMPLICITLY submits on Enter in a single-line text field. Enter
            // is also THE search gesture — without this the farmer types a name, hits
            // Enter expecting results, and instead saves the transaction with no
            // Gegenpartei and closes the modal. Swallowed here rather than on the form,
            // so Enter keeps working as submit in every other field.
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
          />
          {tooShort && (
            <span className="liq-cp__note" role="status">
              Mindestens {COUNTERPARTY_MIN_QUERY_LENGTH} Zeichen eingeben.
            </span>
          )}
          {term && <CounterpartyResults term={term} onPick={pick} />}
        </>
      )}
      <span className="liq-txform__hint">
        Optional — verknüpft die Buchung mit einer Organisation im Betrieb.
      </span>
    </div>
  );
}
